import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import { useInvokeDenaliAssistant, useCancelDenaliAssistant } from '../../../../api/denaliAssistant'
import MessageInput from '../common/MessageInput'
import ClientFocusSelector from '../common/ClientFocusSelector'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import Greetings from '../Landing/Greetings'
import SuggestionList from './SuggestionList'

const useStyles = makeStyles((theme) => ({
  landingContainer: {
    height: '100%',
    width: '100%'
  }
}))

function MobileLandingPage () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const messageInputRef = useRef(null)
  const [hasPrefilledPrompt, setHasPrefilledPrompt] = useState(false)

  const invokeMutation = useInvokeDenaliAssistant()
  const cancelMutation = useCancelDenaliAssistant()
  const cancelledDuringInvokeRef = useRef(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const initialFocus = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const focusType = params.get('focusType')
    const focusSubjectIdentifier = params.get('focusSubjectIdentifier')
    const focusSubjectLabel = params.get('focusSubjectLabel')
    if (focusType === 'client' && focusSubjectIdentifier && focusSubjectLabel) {
      return {
        type: 'client',
        subjectIdentifier: Number(focusSubjectIdentifier) || focusSubjectIdentifier,
        subjectLabel: focusSubjectLabel
      }
    }
    return null
  }, [location.search])

  const [clientFocus, setClientFocus] = useState(initialFocus)

  const focusMode = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('focusMode') || null
  }, [location.search])

  const buildContext = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const context = {
      currentView: 'ai-assistant-chat',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    const skillIdParam = params.get('skillId')
    if (skillIdParam) context.skillId = skillIdParam
    if (clientFocus) {
      context.focus = clientFocus
    } else {
      const focusType = params.get('focusType')
      const focusSubjectIdentifier = params.get('focusSubjectIdentifier')
      const focusSubjectLabel = params.get('focusSubjectLabel')
      if (focusType && focusType !== 'client' && focusSubjectIdentifier && focusSubjectLabel) {
        context.focus = {
          type: focusType,
          subjectIdentifier: focusSubjectIdentifier,
          subjectLabel: focusSubjectLabel
        }
      }
    }
    return context
  }, [location.search, clientFocus])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const prompt = params.get('prompt')
    if (prompt && messageInputRef.current) {
      messageInputRef.current.setValue(prompt)
      setHasPrefilledPrompt(true)
    }
  }, [location.search])

  const greeting = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('greeting') || undefined
  }, [location.search])

  const focusRequired = focusMode === 'client' && !clientFocus

  const handleCancel = useCallback(() => {
    if (invokeMutation.isLoading) {
      cancelledDuringInvokeRef.current = true
      setIsCancelling(true)
    }
  }, [invokeMutation.isLoading])

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim()) return
    if (focusMode === 'client' && !clientFocus) return

    try {
      const result = await invokeMutation.mutateAsync({
        prompt: message,
        attachments,
        context: buildContext,
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        }
      })

      if (cancelledDuringInvokeRef.current) {
        cancelledDuringInvokeRef.current = false
        setIsCancelling(false)
        messageInputRef.current?.resetSending()
        if (result?.threadId) {
          cancelMutation.mutate({ threadId: result.threadId })
        }
        return
      }

      if (result.threadId) {
        history.replace(`/denali-ai/chat/${result.threadId}`)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    }
  }, [invokeMutation, cancelMutation, history, buildContext, focusMode, clientFocus])

  return (
    <div className={classes.landingContainer}>
      <FlexColumn className='gap-md mt-10 items-center' style={{ width: '100%', height: 'calc(100% - 15vh)' }}>
        <FadeIn className='w-full'>
          <Greetings isMobile greeting={greeting} />
        </FadeIn>
        <FadeIn delay={0.2} className='w-full'>
          <div className='px-2'>
            {focusMode !== 'none' && focusMode && (
              <div style={{ marginBottom: '12px' }}>
                <ClientFocusSelector
                  value={clientFocus}
                  onChange={setClientFocus}
                  size='xs'
                  error={focusRequired}
                />
              </div>
            )}
            <MessageInput
              ref={messageInputRef}
              onSend={handleSendMessage}
              onCancel={handleCancel}
              disabled={focusRequired}
              isProcessing={invokeMutation.isLoading}
              isCancelling={isCancelling}
              placeholder='What do you want to know?'
              minRows={3}
            />
          </div>
        </FadeIn>
        {!hasPrefilledPrompt && (
          <FadeIn delay={0.4}>
            <SuggestionList
              take={3}
              onSuggestionClick={(prompt) => {
                messageInputRef.current?.setValue(prompt)
              }}
            />
          </FadeIn>
        )}
      </FlexColumn>
    </div>
  )
}

export default MobileLandingPage
